import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { GraphMeasure } from '../types/graph-data.interface';
import { MeasureState } from '../types/graph-dialog.interface';
import { MeasureConfig } from '../types/measure-config.interface';

@Injectable()
export class MeasureStateManager {
  readonly measureStateX: WritableSignal<MeasureState> = signal({ measure: null, config: null });
  readonly measureStateY: WritableSignal<MeasureState> = signal({ measure: null, config: null });
  readonly measureStateMultiYear: WritableSignal<MeasureState> = signal({ measure: null, config: null });
  readonly measureStatesEvTemp: WritableSignal<MeasureState[]> = signal([]);

  readonly selectedMeasureX = computed(() => this.measureStateX().measure);
  readonly selectedMeasureY = computed(() => this.measureStateY().measure);
  readonly selectedMeasureMultiYear = computed(() => this.measureStateMultiYear().measure);
  readonly selectedMeasuresEvTemp = computed(() =>
    this.measureStatesEvTemp()
      .map(s => s.measure)
      .filter((m): m is GraphMeasure => m !== null),
  );

  // The persisted order (sort_nr) seeds the axes only while they are unassigned:
  // a refresh must not clobber the user's possibly unsaved axis assignment.
  seedXYAxes(orderedMeasureIds: number[], measures: GraphMeasure[], configs?: Map<number, MeasureConfig>): void {
    const xId = this.selectedMeasureX()?.id ?? orderedMeasureIds[0];
    const yId = this.selectedMeasureY()?.id ?? orderedMeasureIds[1];
    const xMeasure = measures.find(m => m.id === xId);
    const yMeasure = measures.find(m => m.id === yId);
    if (xMeasure) this.setMeasure('x', xMeasure, configs);
    if (yMeasure) this.setMeasure('y', yMeasure, configs);
  }

  // Save-order contract: the persisted sort_nr is rewritten to this array's
  // order, so it must reflect the axis assignment — graphMeasures follows the
  // listing order, which on legacy graphs does not match the axes.
  selectedXYIds(): [number, number] | null {
    const x = this.selectedMeasureX();
    const y = this.selectedMeasureY();
    return x && y ? [x.id, y.id] : null;
  }

  setMeasure(axis: 'x' | 'y' | 'multiYear', measure: GraphMeasure | null, configs?: Map<number, MeasureConfig>): void {
    const stateSignal = this.getStateSignal(axis);
    const config = measure ? (configs?.get(measure.id) ?? null) : null;
    stateSignal.set({ measure, config });
  }

  // UAT-014 : choisir pour un axe la mesure déjà posée sur l'autre axe échange
  // les deux — sans cela selectedXYIds vaut [m, m] et le diff de sauvegarde
  // supprime la ligne graphic_measure de l'autre mesure.
  assignXYMeasure(axis: 'x' | 'y', measure: GraphMeasure | null, configs?: Map<number, MeasureConfig>): void {
    const otherAxis = axis === 'x' ? 'y' : 'x';
    if (measure && this.getState(otherAxis).measure?.id === measure.id) {
      this.setMeasure(otherAxis, this.getState(axis).measure, configs);
    }
    this.setMeasure(axis, measure, configs);
  }

  removeMeasure(axis: 'x' | 'y' | 'multiYear'): void {
    const stateSignal = this.getStateSignal(axis);
    stateSignal.set({ measure: null, config: null });
  }

  updateConfig(axis: 'x' | 'y' | 'multiYear', config: MeasureConfig): void {
    const stateSignal = this.getStateSignal(axis);
    stateSignal.update(s => ({ ...s, config }));
  }

  setMeasuresEvTemp(measures: GraphMeasure[], configs?: Map<number, MeasureConfig>): void {
    this.measureStatesEvTemp.set(measures.map(measure => ({ measure, config: configs?.get(measure.id) ?? null })));
  }

  removeMeasureEvTemp(measure: GraphMeasure): void {
    this.measureStatesEvTemp.update(states => states.filter(s => s.measure?.id !== measure.id));
  }

  updateConfigEvTemp(measureId: number, config: MeasureConfig): void {
    this.measureStatesEvTemp.update(states => states.map(s => (s.measure?.id === measureId ? { ...s, config } : s)));
  }

  getState(axis: 'x' | 'y' | 'multiYear'): MeasureState {
    return this.getStateSignal(axis)();
  }

  reset(): void {
    this.measureStateX.set({ measure: null, config: null });
    this.measureStateY.set({ measure: null, config: null });
    this.measureStateMultiYear.set({ measure: null, config: null });
    this.measureStatesEvTemp.set([]);
  }

  sortYears(years: string[]): string[] {
    return years.sort((a, b) => {
      const isSpecialA = isNaN(Number(a));
      const isSpecialB = isNaN(Number(b));

      if (isSpecialA && !isSpecialB) return 1;
      if (!isSpecialA && isSpecialB) return -1;
      if (isSpecialA && isSpecialB) {
        const offsetA = this.relativeYearOffset(a);
        const offsetB = this.relativeYearOffset(b);
        if (offsetA === null || offsetB === null) return a.localeCompare(b);
        return offsetA - offsetB;
      }

      return Number(a) - Number(b);
    });
  }

  // Years restored from persisted metadata: migrate legacy 'N' tokens to 'A', then order.
  normalizeStoredYears(years: string[]): string[] {
    return this.sortYears(years.map(y => (y.toUpperCase().startsWith('N') ? 'A' + y.substring(1) : y)));
  }

  // Relative-year offset: 'A'/'N' = 0, 'A-N' = N. null when unparseable.
  private relativeYearOffset(token: string): number | null {
    const match = /^[AaNn](?:-(\d+))?$/.exec(token);
    if (!match) return null;
    return match[1] ? Number(match[1]) : 0;
  }

  private getStateSignal(axis: 'x' | 'y' | 'multiYear'): WritableSignal<MeasureState> {
    switch (axis) {
      case 'x':
        return this.measureStateX;
      case 'y':
        return this.measureStateY;
      case 'multiYear':
        return this.measureStateMultiYear;
    }
  }
}
